import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { 
    getAuth, 
    onAuthStateChanged, 
    signInWithEmailAndPassword, 
    signOut as firebaseSignOut,
    createUserWithEmailAndPassword,
    signInAnonymously
} from 'firebase/auth';
import { 
    getFirestore, 
    doc, 
    getDoc, 
    setDoc, 
    collection, 
    onSnapshot,
    addDoc,
    deleteDoc,
    query,
    getDocs
} from 'firebase/firestore';
import { Sun, Moon, BookOpen, Upload, Download, Smartphone, Tv, Laptop, Globe, Apple, Trash2, Edit, PlusCircle, Settings, LogOut, Eye, X } from 'lucide-react';

// --- C O N F I G U R A C I Ó N   D E   F I R E B A S E ---
const firebaseConfig = {
  apiKey: "AIzaSyCiz_-eUjVaNzVS5xozb9ReTlKQSeShHFs",
  authDomain: "tupp-e56f0.firebaseapp.com",
  projectId: "tupp-e56f0",
  storageBucket: "tupp-e56f0.appspot.com",
  messagingSenderId: "142122008289",
  appId: "1:142122008289:web:e444b4a45b2236c59341a0",
  measurementId: "G-TY3KXEEXFE"
};

// --- C R E D E N C I A L E S   D E L   A D M I N I S T R A D O R ---
const ADMIN_EMAIL = "admin@tuapp.com";
const ADMIN_PASSWORD = "passwordseguro123";

// --- I N I C I A L I Z A C I Ó N   D E   F I R E B A S E ---
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// --- H O O K S   P E R S O N A L I Z A D O S ---
function usePlatformSettings() {
    const [settings, setSettings] = useState({
        name: 'Mi Catálogo',
        color: '#4f46e5',
        logoUrl: 'https://placehold.co/100x100/4f46e5/ffffff?text=Logo',
        faviconUrl: 'https://placehold.co/32x32/4f46e5/ffffff?text=F',
        typography: 'Inter',
        whatsapp: '',
        telegram: '',
        tutorials_enabled: false,
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const settingsRef = doc(db, 'settings', 'platform');
        const unsubscribe = onSnapshot(settingsRef, (docSnap) => {
            if (docSnap.exists()) {
                setSettings(prev => ({...prev, ...docSnap.data()}));
            }
            setLoading(false);
        }, (error) => {
            console.error("Error fetching platform settings:", error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const saveSettings = async (newSettings) => {
        const settingsRef = doc(db, 'settings', 'platform');
        await setDoc(settingsRef, newSettings, { merge: true });
    };

    return { settings, saveSettings, loadingSettings: loading };
}

function useProducts() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const q = query(collection(db, 'products'));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const productsData = [];
            querySnapshot.forEach((doc) => {
                productsData.push({ id: doc.id, ...doc.data() });
            });
            setProducts(productsData);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching products:", error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);
    
    const addProduct = async (productData) => await addDoc(collection(db, 'products'), productData);
    const updateProduct = async (id, productData) => await setDoc(doc(db, 'products', id), productData, { merge: true });
    const deleteProduct = async (id) => await deleteDoc(doc(db, 'products', id));

    return { products, addProduct, updateProduct, deleteProduct, loadingProducts: loading };
}

function useTutorials() {
    const [tutorials, setTutorials] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const q = query(collection(db, 'tutorials'));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const tutorialsData = [];
            querySnapshot.forEach((doc) => {
                tutorialsData.push({ id: doc.id, ...doc.data() });
            });
            setTutorials(tutorialsData);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const addTutorial = async (data) => await addDoc(collection(db, 'tutorials'), data);
    const updateTutorial = async (id, data) => await setDoc(doc(db, 'tutorials', id), data, { merge: true });
    const deleteTutorial = async (id) => await deleteDoc(doc(db, 'tutorials', id));
    
    return { tutorials, addTutorial, updateTutorial, deleteTutorial, loadingTutorials: loading };
}

// --- C O M P O N E N T E S   D E   L A   A P L I C A C I Ó N ---

const CategoryIcon = ({ category, className }) => {
    const iconProps = { className: className || "w-5 h-5" };
    switch (category) {
        case 'Android': return <Smartphone {...iconProps} />;
        case 'Roku': return <Tv {...iconProps} />;
        case 'Windows': return <Laptop {...iconProps} />;
        case 'WebPlayer': return <Globe {...iconProps} />;
        case 'iOS': return <Apple {...iconProps} />;
        default: return <Smartphone {...iconProps} />;
    }
};

const ProductCard = ({ product, onSelect, themeColor }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const safeImageUrls = useMemo(() => 
        Array.isArray(product.imageUrls) && product.imageUrls.length > 0
            ? product.imageUrls.filter(url => typeof url === 'string' && url.trim() !== '')
            : ['https://placehold.co/600x400/cccccc/ffffff?text=Sin+Imagen']
    , [product.imageUrls]);
    
    // NEW: Automatic image carousel effect
    useEffect(() => {
        if (safeImageUrls.length > 1) {
            const timer = setInterval(() => {
                setCurrentImageIndex(prevIndex => (prevIndex + 1) % safeImageUrls.length);
            }, 4000); // Change image every 4 seconds
            return () => clearInterval(timer);
        }
    }, [safeImageUrls.length]);


    return (
        <div 
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-300 cursor-pointer group"
            onClick={() => onSelect(product)}
        >
            <div className="relative h-56">
                {safeImageUrls.map((url, index) => (
                    <img 
                        key={index}
                        src={url} 
                        alt={`${product.name} - Imagen ${index + 1}`} 
                        className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-1000 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'}`}
                        onError={(e) => e.currentTarget.src = 'https://placehold.co/600x400/cccccc/ffffff?text=Error'}
                    />
                ))}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/70 to-transparent p-4">
                     <h3 className="text-xl font-bold text-white">{product.name}</h3>
                </div>
                <div className="absolute top-2 right-2 bg-white/20 backdrop-blur-sm text-white p-2 rounded-full">
                    <CategoryIcon category={product.category} className="w-5 h-5" />
                </div>
            </div>
            <div className="p-4">
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-2 h-10 overflow-hidden">{product.description}</p>
                 <div className="flex justify-between items-center mt-4">
                    <p className="text-lg font-semibold" style={{color: themeColor}}>
                        {product.rebrandingPrice ? `$${product.rebrandingPrice} USD` : 'Consultar'}
                    </p>
                    <button className="text-white font-bold py-2 px-4 rounded-lg transition-colors duration-300" style={{backgroundColor: themeColor}}>
                        Ver Detalles
                    </button>
                </div>
            </div>
        </div>
    );
};

const ProductDetailModal = ({ product, onClose, themeColor, settings }) => {
    // Hooks moved to the top level to be called unconditionally.
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const safeImageUrls = useMemo(() => {
        if (!product || !Array.isArray(product.imageUrls) || product.imageUrls.length === 0) {
            return ['https://placehold.co/800x600/cccccc/ffffff?text=Sin+Imagen'];
        }
        return product.imageUrls.filter(url => typeof url === 'string' && url.trim() !== '');
    }, [product]);
    
    const whatsappUrl = useMemo(() => {
        if (!product || !settings?.whatsapp || !product?.name) {
             return '';
        }
        const message = `Hola, quiero un demo para el rebranding de la aplicación "${product.name}"`;
        const cleanWhatsappNumber = settings.whatsapp.replace(/[^0-9]/g, '');
        return `https://wa.me/${cleanWhatsappNumber}?text=${encodeURIComponent(message)}`;
    }, [settings, product]);

    // Reset image index when product changes
    useEffect(() => {
        setCurrentImageIndex(0);
    }, [product]);

    const nextImage = () => setCurrentImageIndex(prev => (prev + 1) % safeImageUrls.length);
    const prevImage = () => setCurrentImageIndex(prev => (prev - 1 + safeImageUrls.length) % safeImageUrls.length);

    // Conditional return is now AFTER all hooks have been called.
    if (!product) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 p-4" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto flex flex-col md:flex-row" onClick={e => e.stopPropagation()}>
                {/* Image carousel for product details */}
                <div className="md:w-1/2 relative group">
                   <img src={safeImageUrls[currentImageIndex]} alt={product.name} className="w-full h-64 md:h-full object-cover rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none"/>
                   {safeImageUrls.length > 1 && (
                       <>
                        <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full opacity-50 group-hover:opacity-100 transition-opacity">‹</button>
                        <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full opacity-50 group-hover:opacity-100 transition-opacity">›</button>
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2">
                            {safeImageUrls.map((_, idx) => (
                                <div key={idx} className={`w-2 h-2 rounded-full ${idx === currentImageIndex ? 'bg-white' : 'bg-white/50'}`}></div>
                            ))}
                        </div>
                       </>
                   )}
                </div>
                <div className="md:w-1/2 p-6 flex flex-col">
                    <div className="flex justify-between items-start">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{product.name}</h2>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-800 dark:hover:text-white"><X /></button>
                    </div>
                     <div className="flex items-center gap-2 mb-4 text-gray-500 dark:text-gray-400">
                        <CategoryIcon category={product.category} />
                        <span>{product.category}</span>
                    </div>

                    <div className="flex-grow space-y-4 text-gray-700 dark:text-gray-300">
                        <div>
                            <h4 className="font-semibold text-lg mb-1" style={{color: themeColor}}>Descripción</h4>
                            <p>{product.description}</p>
                        </div>
                        {product.features && product.features.length > 0 && (
                            <div>
                                <h4 className="font-semibold text-lg mb-2" style={{color: themeColor}}>Características</h4>
                                <ul className="list-disc list-inside space-y-1">
                                    {product.features.map((feature, index) => <li key={index}>{feature}</li>)}
                                </ul>
                            </div>
                        )}
                        <div>
                             <h4 className="font-semibold text-lg mb-1" style={{color: themeColor}}>Precio de Rebranding</h4>
                             <p className="text-2xl font-bold">{product.rebrandingPrice ? `$${product.rebrandingPrice} USD` : 'A consultar'}</p>
                        </div>
                    </div>
                    <div className="mt-6 space-y-3">
                         {product.downloaderCode && (
                            <div className="text-center">
                                <p className="text-sm text-gray-500 dark:text-gray-400">Usa el código en Downloader:</p>
                                <p className="text-xl font-mono bg-gray-100 dark:bg-gray-700 p-2 rounded-lg tracking-widest font-bold text-orange-500">{product.downloaderCode}</p>
                            </div>
                         )}
                         {product.downloadLink && (
                           <a href={product.downloadLink} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 text-white font-bold py-3 px-4 rounded-lg transition-colors duration-300" style={{backgroundColor: themeColor}}>
                               <Download /> Descargar App
                           </a>
                         )}
                         {whatsappUrl && (
                             <a 
                                href={whatsappUrl} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="w-full flex items-center justify-center gap-2 bg-green-500 text-white font-bold py-3 px-4 rounded-lg transition-colors duration-300 hover:bg-green-600"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99 0-3.902-.539-5.586-1.54l-6.167 1.688a.753.753 0 0 1-.947-.947zM6.54 6.829c.182-.433.366-.442.533-.45.14-.008.307-.008.452.008.182.01.433.21.618.688.184.48.65.1634.998.1502.348.147.96.113 1.52.709.562.596.814.845.962.993.148.148.296.182.48.034.184-.148.433-.48.618-.688.184-.208.367-.184.618-.104.25.08.562.113.845.147.284.034.452.008.533-.182.08-.189-.533-1.002-1.04-1.804-.505-.802-1.01-1.133-1.413-1.317-.404-.184-.845-.278-1.52-.278-1.01 0-1.804.232-2.366.845-.562.612-.878 1.412-.878 2.365 0 .953.232 1.804.878 2.365.644.562 1.412.878 2.365.878h.034c.48 0 1.01-.148 1.413-.48.404-.332.618-.78.78-1.24.162-.459.138-.877.08-1.316-.058-.44-.148-.562-.278-.644-.13-.08-.278-.058-.452.034l-.845.48c-.208.113-.452.184-.644.113-.19-.07-.367-.208-.48-.366-.114-.16-.08-.348.034-.534z"></path></svg>
                                Solicitar Demo
                             </a>
                         )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- COMPONENTES DE TUTORIALES ---

const TutorialCard = ({ tutorial, onSelect }) => {
    const getYouTubeId = (url) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };
    const videoId = getYouTubeId(tutorial.mediaUrl || '');

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-300 group">
            <div className="relative h-48 bg-gray-200 dark:bg-gray-700">
                {videoId ? (
                    <img src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`} alt={tutorial.title} className="w-full h-full object-cover"/>
                ) : (
                    <img src={tutorial.mediaUrl || 'https://placehold.co/600x400/cccccc/ffffff?text=Media'} alt={tutorial.title} className="w-full h-full object-cover"/>
                )}
                 <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                     <button onClick={() => onSelect(tutorial)} className="text-white bg-indigo-600/80 py-2 px-4 rounded-lg font-semibold">Ver Tutorial</button>
                 </div>
            </div>
            <div className="p-4">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">{tutorial.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm h-16 overflow-hidden">{tutorial.description}</p>
            </div>
        </div>
    );
};

const TutorialDetailModal = ({ tutorial, onClose }) => {
    if (!tutorial) return null;

    const getYouTubeEmbedUrl = (url) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        const videoId = (match && match[2].length === 11) ? match[2] : null;
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    };
    const embedUrl = getYouTubeEmbedUrl(tutorial.mediaUrl || '');

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 p-4" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                 <div className="p-4 flex justify-between items-center border-b dark:border-gray-700">
                     <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{tutorial.title}</h2>
                     <div className="flex items-center gap-4">
                        {embedUrl && <a href={tutorial.mediaUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Ver en YouTube</a>}
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-800 dark:hover:text-white"><X /></button>
                     </div>
                 </div>
                 <div className="p-6">
                    {/* Responsive video container */}
                    <div className="relative mb-4" style={{ paddingTop: '56.25%' }}>
                        {embedUrl ? (
                            <iframe 
                                src={embedUrl}
                                frameBorder="0" 
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                allowFullScreen
                                className="absolute top-0 left-0 w-full h-full rounded-lg"
                                title={tutorial.title}>
                           </iframe>
                        ) : (
                           tutorial.mediaUrl && <img src={tutorial.mediaUrl} alt={tutorial.title} className="absolute top-0 left-0 w-full h-full object-contain rounded-lg"/>
                        )}
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{tutorial.description}</p>
                 </div>
            </div>
        </div>
    );
};

const HomePage = ({ settings, products, loadingProducts, tutorials, loadingTutorials }) => {
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedTutorial, setSelectedTutorial] = useState(null);

    if (loadingProducts) {
        return <div className="flex justify-center items-center h-screen bg-gray-100 dark:bg-gray-900 text-white">Cargando...</div>
    }

    return (
        <div className="bg-gray-100 dark:bg-gray-900 min-h-screen">
            <main className="container mx-auto px-4 py-8">
                <h2 className="text-3xl md:text-4xl font-extrabold text-center text-gray-800 dark:text-white mb-12">
                    Nuestro Catálogo de Aplicaciones
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                    {products.map(product => (
                        <ProductCard 
                            key={product.id} 
                            product={product} 
                            onSelect={setSelectedProduct}
                            themeColor={settings.color}
                        />
                    ))}
                </div>
                {settings.tutorials_enabled && (
                    <div id="tutorials-section" className="mt-20 pt-10 border-t border-gray-200 dark:border-gray-700">
                        <h2 className="text-3xl md:text-4xl font-extrabold text-center text-gray-800 dark:text-white mb-12">
                            Tutoriales
                        </h2>
                        {loadingTutorials ? (
                            <p className="text-center text-white">Cargando tutoriales...</p>
                        ) : (
                             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                                {tutorials.map(tutorial => (
                                    <TutorialCard key={tutorial.id} tutorial={tutorial} onSelect={setSelectedTutorial} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>
            
            <ProductDetailModal product={selectedProduct} onClose={() => setSelectedProduct(null)} themeColor={settings.color} settings={settings}/>
            <TutorialDetailModal tutorial={selectedTutorial} onClose={() => setSelectedTutorial(null)} />
        </div>
    );
};

// --- RESTORED COMPONENTS START HERE ---

const ProductFormModal = ({ isOpen, onClose, onSave, product }) => {
    const [formData, setFormData] = useState({});

    useEffect(() => {
        if(isOpen) {
            if (product) {
                setFormData({
                    ...product,
                    imageUrls: Array.isArray(product.imageUrls) ? product.imageUrls.join('\n') : '',
                    features: Array.isArray(product.features) ? product.features.join('\n') : '',
                });
            } else {
                 setFormData({ name: '', description: '', imageUrls: '', features: '', downloadLink: '', downloaderCode: '', category: 'Android', rebrandingPrice: '' });
            }
        }
    }, [product, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const dataToSave = {
            ...formData,
            rebrandingPrice: parseFloat(formData.rebrandingPrice) || 0,
            imageUrls: formData.imageUrls.split('\n').filter(url => url.trim() !== ''),
            features: formData.features.split('\n').filter(f => f.trim() !== ''),
        };
        onSave(dataToSave);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] overflow-hidden flex flex-col">
                <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white">{product ? 'Editar Producto' : 'Agregar Producto'}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800 dark:hover:text-white"><X/></button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
                    <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nombre de la App</label><input type="text" name="name" value={formData.name || ''} onChange={handleChange} className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0" required /></div>
                    <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Descripción</label><textarea name="description" value={formData.description || ''} onChange={handleChange} rows="3" className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0"></textarea></div>
                    <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">URLs de Imágenes (una por línea)</label><textarea name="imageUrls" value={formData.imageUrls || ''} onChange={handleChange} rows="3" className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0"></textarea></div>
                    <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Características (una por línea)</label><textarea name="features" value={formData.features || ''} onChange={handleChange} rows="4" className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0"></textarea></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Enlace de Descarga</label><input type="url" name="downloadLink" value={formData.downloadLink || ''} onChange={handleChange} className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0" /></div>
                        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Código de Downloader</label><input type="text" name="downloaderCode" value={formData.downloaderCode || ''} onChange={handleChange} className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0" /></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Categoría</label><select name="category" value={formData.category || 'Android'} onChange={handleChange} className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0"><option>Android</option><option>Roku</option><option>Windows</option><option>WebPlayer</option><option>iOS</option></select></div>
                        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Precio Rebranding (USD)</label><input type="number" name="rebrandingPrice" value={formData.rebrandingPrice || ''} onChange={handleChange} className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0" /></div>
                    </div>
                    <div className="pt-4 flex justify-end gap-3"><button type="button" onClick={onClose} className="py-2 px-4 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 dark:bg-gray-600 dark:text-white dark:hover:bg-gray-500">Cancelar</button><button type="submit" className="py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">Guardar Producto</button></div>
                </form>
            </div>
        </div>
    );
};

const TutorialFormModal = ({ isOpen, onClose, onSave, tutorial }) => {
    const [formData, setFormData] = useState({ title: '', description: '', mediaUrl: ''});

    useEffect(() => {
        if(isOpen) {
            if (tutorial) {
                setFormData(tutorial);
            } else {
                 setFormData({ title: '', description: '', mediaUrl: ''});
            }
        }
    }, [tutorial, isOpen]);
    
    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };
    
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] overflow-hidden flex flex-col">
                <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white">{tutorial ? 'Editar Tutorial' : 'Agregar Tutorial'}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800 dark:hover:text-white"><X/></button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
                     <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Título</label><input type="text" name="title" value={formData.title} onChange={handleChange} className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0" required /></div>
                     <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">URL de Imagen o Video (YouTube)</label><input type="url" name="mediaUrl" value={formData.mediaUrl} onChange={handleChange} className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0" required /></div>
                     <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Descripción</label><textarea name="description" value={formData.description} onChange={handleChange} rows="8" className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0"></textarea></div>
                    <div className="pt-4 flex justify-end gap-3"><button type="button" onClick={onClose} className="py-2 px-4 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 dark:bg-gray-600 dark:text-white dark:hover:bg-gray-500">Cancelar</button><button type="submit" className="py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">Guardar Tutorial</button></div>
                </form>
            </div>
        </div>
    );
};

const AdminPanel = ({ settings, saveSettings, products, addProduct, updateProduct, deleteProduct, tutorials, addTutorial, updateTutorial, deleteTutorial }) => {
    const [currentTab, setCurrentTab] = useState('products');
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [isTutorialModalOpen, setIsTutorialModalOpen] = useState(false);
    const [editingTutorial, setEditingTutorial] = useState(null);
    const [localSettings, setLocalSettings] = useState(settings);

    useEffect(() => { setLocalSettings(settings); }, [settings]);
    
    const handleSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;
        setLocalSettings(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSaveSettings = () => { saveSettings(localSettings); alert("Configuración guardada!"); };
    
    // Product Modal Handlers
    const openAddProductModal = () => { setEditingProduct(null); setIsProductModalOpen(true); };
    const openEditProductModal = (product) => { setEditingProduct(product); setIsProductModalOpen(true); };
    const closeProductModal = () => setIsProductModalOpen(false);
    const handleSaveProduct = (data) => {
        if (editingProduct) { updateProduct(editingProduct.id, data); } 
        else { addProduct(data); }
        closeProductModal();
    };
    const handleDeleteProduct = (id) => { if (window.confirm("¿Estás seguro?")) { deleteProduct(id); }};

    // Tutorial Modal Handlers
    const openAddTutorialModal = () => { setEditingTutorial(null); setIsTutorialModalOpen(true); };
    const openEditTutorialModal = (tutorial) => { setEditingTutorial(tutorial); setIsTutorialModalOpen(true); };
    const closeTutorialModal = () => setIsTutorialModalOpen(false);
    const handleSaveTutorial = (data) => {
        if (editingTutorial) { updateTutorial(editingTutorial.id, data); } 
        else { addTutorial(data); }
        closeTutorialModal();
    };
    const handleDeleteTutorial = (id) => { if (window.confirm("¿Estás seguro?")) { deleteTutorial(id); }};


    return (
        <div className="bg-gray-100 dark:bg-gray-900 min-h-screen text-gray-800 dark:text-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <h1 className="text-3xl font-bold mb-6">Panel de Administración</h1>

                <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                    <nav className="-mb-px flex space-x-8">
                        <button onClick={() => setCurrentTab('products')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'products' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Gestionar Productos</button>
                        <button onClick={() => setCurrentTab('tutorials')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'tutorials' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Gestionar Tutoriales</button>
                        <button onClick={() => setCurrentTab('settings')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'settings' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Configuración General</button>
                    </nav>
                </div>
                
                {currentTab === 'products' && (
                    <div>
                        <div className="flex justify-end mb-4"><button onClick={openAddProductModal} className="inline-flex items-center gap-2 bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors"><PlusCircle size={20} />Agregar Producto</button></div>
                        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                               <thead className="bg-gray-50 dark:bg-gray-700"><tr><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">App</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Categoría</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Precio Rebranding</th><th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Acciones</th></tr></thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {products.map(p => (<tr key={p.id}><td className="px-6 py-4 whitespace-nowrap"><div className="flex items-center"><div className="flex-shrink-0 h-10 w-10"><img className="h-10 w-10 rounded-full object-cover" src={p.imageUrls?.[0] || 'https://placehold.co/40x40'} alt={p.name} /></div><div className="ml-4"><div className="text-sm font-medium text-gray-900 dark:text-white">{p.name}</div></div></div></td><td className="px-6 py-4 whitespace-nowrap"><span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">{p.category}</span></td><td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">${p.rebrandingPrice || 'N/A'}</td><td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"><button onClick={() => openEditProductModal(p)} className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-200 mr-4"><Edit size={18}/></button><button onClick={() => deleteProduct(p.id)} className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-200"><Trash2 size={18}/></button></td></tr>))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {currentTab === 'tutorials' && (
                    <div>
                        <div className="flex justify-end mb-4"><button onClick={openAddTutorialModal} className="inline-flex items-center gap-2 bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors"><PlusCircle size={20} />Agregar Tutorial</button></div>
                         <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                               <thead className="bg-gray-50 dark:bg-gray-700"><tr><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Título</th><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Descripción Breve</th><th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Acciones</th></tr></thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {tutorials.map(t => (<tr key={t.id}><td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{t.title}</td><td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-300 max-w-sm truncate">{t.description}</td><td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"><button onClick={() => openEditTutorialModal(t)} className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-200 mr-4"><Edit size={18}/></button><button onClick={() => deleteTutorial(t.id)} className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-200"><Trash2 size={18}/></button></td></tr>))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {currentTab === 'settings' && (
                     <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nombre de la Plataforma</label><input type="text" name="name" value={localSettings.name} onChange={handleSettingsChange} className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0" /></div>
                             <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Color Principal</label><input type="color" name="color" value={localSettings.color} onChange={handleSettingsChange} className="mt-1 block w-full h-10 rounded-md" /></div>
                            <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">URL del Logo</label><input type="url" name="logoUrl" value={localSettings.logoUrl} onChange={handleSettingsChange} className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0" /></div>
                            <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">URL del Favicon</label><input type="url" name="faviconUrl" value={localSettings.faviconUrl} onChange={handleSettingsChange} className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0" /></div>
                             <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tipografía (Nombre de Google Font)</label><input type="text" name="typography" value={localSettings.typography} onChange={handleSettingsChange} className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0" /></div>
                             <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Número de WhatsApp (con cód. país)</label><input type="text" name="whatsapp" value={localSettings.whatsapp} onChange={handleSettingsChange} placeholder="ej: 5491112345678" className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0" /></div>
                             <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Usuario de Telegram</label><input type="text" name="telegram" value={localSettings.telegram} onChange={handleSettingsChange} placeholder="ej: miusuario" className="mt-1 block w-full rounded-md bg-gray-100 dark:bg-gray-700 border-transparent focus:border-indigo-500 focus:bg-white focus:ring-0" /></div>
                             <div className="flex items-center"><input id="tutorials_enabled" name="tutorials_enabled" type="checkbox" checked={localSettings.tutorials_enabled} onChange={handleSettingsChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" /><label htmlFor="tutorials_enabled" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">Activar Sección de Tutoriales</label></div>
                        </div>
                        <div className="mt-6 flex justify-end"><button onClick={handleSaveSettings} className="bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors">Guardar Configuración</button></div>
                    </div>
                )}
                
                <ProductFormModal isOpen={isProductModalOpen} onClose={() => setIsProductModalOpen(false)} onSave={handleSaveProduct} product={editingProduct}/>
                <TutorialFormModal isOpen={isTutorialModalOpen} onClose={() => setIsTutorialModalOpen(false)} onSave={handleSaveTutorial} tutorial={editingTutorial}/>
            </div>
        </div>
    );
};

const LoginPage = ({ onLogin }) => {
    const [email, setEmail] = useState(ADMIN_EMAIL);
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        try { await onLogin(email, password); } 
        catch (err) {
            let message = "Ocurrió un error. Inténtalo de nuevo.";
            if (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') { message = "Email o contraseña incorrectos."; }
            setError(message);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
            <div className="w-full max-w-md p-8 space-y-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg"><h1 className="text-3xl font-bold text-center text-gray-900 dark:text-white">Iniciar Sesión</h1><p className="text-center text-sm text-gray-500 dark:text-gray-400">Acceso al panel de administración.</p>
                <form className="space-y-6" onSubmit={handleSubmit}>
                    <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 block w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-transparent rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"/></div>
                    <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contraseña</label><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 block w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-transparent rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"/></div>
                    {error && <p className="text-sm text-red-500 text-center">{error}</p>}
                    <div><button type="submit" className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">Entrar</button></div>
                </form>
            </div>
        </div>
    );
};


const Header = ({ settings, user, onNavigate, onSignOut, toggleTheme, theme }) => {
    return (
        <header className="bg-white dark:bg-gray-800 shadow-md sticky top-0 z-40">
            <div className="container mx-auto px-4 py-3 flex justify-between items-center">
                <div className="flex items-center gap-3 cursor-pointer" onClick={() => onNavigate('home')}>
                    <img src={settings.logoUrl} alt="Logo" className="h-10 w-10 rounded-full" />
                    <span className="text-xl font-bold text-gray-800 dark:text-white">{settings.name}</span>
                </div>
                <nav className="flex items-center gap-4">
                    <button onClick={() => onNavigate('home')} className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">Catalogo</button>
                    {settings.tutorials_enabled && <button onClick={() => onNavigate('home', { scrollTo: 'tutorials' })} className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400">Tutoriales</button>}
                    <button onClick={toggleTheme} className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                        {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
                    </button>
                    {user && !user.isAnonymous ? (
                        <>
                            <button onClick={() => onNavigate('admin')} className="hidden sm:inline-flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"><Settings size={18}/> Panel</button>
                            <button onClick={onSignOut} className="inline-flex items-center gap-2 bg-red-500 text-white font-semibold py-2 px-4 rounded-lg hover:bg-red-600 transition-colors"><LogOut size={18} /> <span className="hidden sm:inline">Salir</span></button>
                        </>
                    ) : (
                        <button onClick={() => onNavigate('login')} className="bg-indigo-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors">Iniciar Sesión</button>
                    )}
                </nav>
            </div>
        </header>
    );
};


const FloatingContactButtons = ({ settings }) => {
    if (!settings.whatsapp && !settings.telegram) return null;
    return (
        <div className="fixed bottom-5 right-5 z-30 flex flex-col gap-3">
             {settings.whatsapp && (<a href={`https://wa.me/${settings.whatsapp}`} target="_blank" rel="noopener noreferrer" className="bg-green-500 text-white w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 transform hover:scale-110 transition-all"><svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99 0-3.902-.539-5.586-1.54l-6.167 1.688a.753.753 0 0 1-.947-.947zM6.54 6.829c.182-.433.366-.442.533-.45.14-.008.307-.008.452.008.182.01.433.21.618.688.184.48.65.1634.998.1502.348.147.96.113 1.52.709.562.596.814.845.962.993.148.148.296.182.48.034.184-.148.433-.48.618-.688.184-.208.367-.184.618-.104.25.08.562.113.845.147.284.034.452.008.533-.182.08-.189-.533-1.002-1.04-1.804-.505-.802-1.01-1.133-1.413-1.317-.404-.184-.845-.278-1.52-.278-1.01 0-1.804.232-2.366.845-.562.612-.878 1.412-.878 2.365 0 .953.232 1.804.878 2.365.644.562 1.412.878 2.365.878h.034c.48 0 1.01-.148 1.413-.48.404-.332.618-.78.78-1.24.162-.459.138-.877.08-1.316-.058-.44-.148-.562-.278-.644-.13-.08-.278-.058-.452.034l-.845.48c-.208.113-.452.184-.644.113-.19-.07-.367-.208-.48-.366-.114-.16-.08-.348.034-.534z"></path></svg></a>)}
             {settings.telegram && (<a href={`https://t.me/${settings.telegram}`} target="_blank" rel="noopener noreferrer" className="bg-blue-500 text-white w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:bg-blue-600 transform hover:scale-110 transition-all"><svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.65 12c-.88-.25-.88-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15.91L18.22 18.2c-.27.66-.75.81-1.42.51l-4.54-3.34-2.14 2.05c-.23.24-.43.42-.8.42z"></path></svg></a>)}
        </div>
    );
};

// --- C O M P O N E N T E   P R I N C I P A L   ( A P P ) ---
export default function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState('home');
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');

    const { settings, saveSettings, loadingSettings } = usePlatformSettings();
    const { products, addProduct, updateProduct, deleteProduct, loadingProducts } = useProducts();
    const { tutorials, addTutorial, updateTutorial, deleteTutorial, loadingTutorials } = useTutorials();

    const toggleTheme = () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        localStorage.setItem('theme', newTheme);
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                setLoading(false);
            } else {
                signInAnonymously(auth).catch((error) => console.error("Error signing in anonymously:", error));
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        let favicon = document.querySelector("link[rel*='icon']");
        if (favicon) {
            favicon.href = settings.faviconUrl;
        } else {
            favicon = document.createElement('link');
            favicon.rel = 'shortcut icon';
            favicon.href = settings.faviconUrl;
            document.head.appendChild(favicon);
        }
        document.title = settings.name;
        const fontLink = document.getElementById('google-font-link');
        if (fontLink) {
            fontLink.href = `https://fonts.googleapis.com/css2?family=${settings.typography.replace(' ', '+')}:wght@400;500;600;700;800&display=swap`;
        }
        document.body.style.fontFamily = `'${settings.typography}', sans-serif`;
    }, [settings]);

    useEffect(() => {
        const root = window.document.documentElement;
        root.classList.remove(theme === 'light' ? 'dark' : 'light');
        root.classList.add(theme);
    }, [theme]);

    const handleNavigate = (page, options = {}) => {
        setCurrentPage(page);
        if (page === 'home' && options.scrollTo) {
            setTimeout(() => {
                const section = document.getElementById(`${options.scrollTo}-section`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    };
    
    const handleLogin = useCallback(async (email, password) => {
        try {
            await signInWithEmailAndPassword(auth, email, password);
            setCurrentPage('admin');
        } catch (error) {
            if (error.code === 'auth/user-not-found' && email === ADMIN_EMAIL) {
                try {
                    await createUserWithEmailAndPassword(auth, email, password);
                    const settingsSnap = await getDoc(doc(db, "settings", "platform"));
                    const productsQuery = await getDocs(query(collection(db, "products")));
                    const tutorialsQuery = await getDocs(query(collection(db, "tutorials")));
                    if (!settingsSnap.exists()) {
                        await setDoc(doc(db, "settings", "platform"), { name: 'Mi Catálogo de Apps', color: '#4f46e5', logoUrl: 'https://placehold.co/100x100/4f46e5/ffffff?text=Logo', faviconUrl: 'https://placehold.co/32x32/4f46e5/ffffff?text=F', typography: 'Inter', whatsapp: '1234567890', telegram: 'miUsuarioTelegram', tutorials_enabled: true });
                    }
                    if (productsQuery.empty) {
                        await addProduct({ name: "App de Streaming", description: "Una potente aplicación para ver tus series y películas favoritas.", imageUrls: ["https://images.unsplash.com/photo-1593720213428-28a5b9e94613?q=80&w=2070&auto=format&fit=crop"], features: ["Calidad HD y 4K", "Multi-idioma"], downloadLink: "#", downloaderCode: "12345", category: "Android", rebrandingPrice: 250 });
                        await addProduct({ name: "Juego de Aventura", description: "Explora mundos mágicos y derrota enemigos.", imageUrls: ["https://images.unsplash.com/photo-1555864449-41a948286a11?q=80&w=2070&auto=format&fit=crop"], features: ["Mundo abierto", "Personajes personalizables"], downloadLink: "#", downloaderCode: "67890", category: "Windows", rebrandingPrice: 500 });
                    }
                    if(tutorialsQuery.empty) {
                        await addTutorial({ title: "Cómo instalar la App en tu TV", description: "Sigue estos sencillos pasos para tener tu aplicación funcionando en minutos.", mediaUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ" });
                    }
                    setCurrentPage('admin');
                } catch (createError) {
                    console.error("Error creating admin user:", createError);
                    throw createError;
                }
            } else {
                 console.error("Login error:", error);
                 throw error;
            }
        }
    }, [addProduct, addTutorial]);
    
    const handleSignOut = async () => {
        await firebaseSignOut(auth);
        setCurrentPage('home');
    };
    
    const renderContent = () => {
        if (loading || loadingSettings || !user) {
            return <div className="flex justify-center items-center h-screen bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-white">Cargando...</div>;
        }
        
        if (currentPage === 'login') return <LoginPage onLogin={handleLogin} />;
        
        if (currentPage === 'admin' && user && !user.isAnonymous) {
            return <AdminPanel {...{settings, saveSettings, products, addProduct, updateProduct, deleteProduct, tutorials, addTutorial, updateTutorial, deleteTutorial}}/>;
        }

        return <HomePage 
            settings={settings} 
            products={products} 
            loadingProducts={loadingProducts}
            tutorials={tutorials}
            loadingTutorials={loadingTutorials}
        />;
    }

    return (
        <div className="transition-colors duration-300">
            {currentPage !== 'login' && <Header settings={settings} user={user} onNavigate={handleNavigate} onSignOut={handleSignOut} toggleTheme={toggleTheme} theme={theme}/>}
            {renderContent()}
            {currentPage === 'home' && <FloatingContactButtons settings={settings} />}
        </div>
    );
}


